import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Download, ExternalLink, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react'
import { useMutation } from '@tanstack/react-query'
import { api } from '../lib/api'
import toast from 'react-hot-toast'

interface PreviewItem {
  ml_item_id: string
  variation_id: number | null
  title: string
  seller_sku: string | null
  quantity: number
  unit_price: number
}

interface Preview {
  order_id: string
  status_ml: string
  total: number
  currency: string
  date_created: string
  buyer_nickname: string
  items: PreviewItem[]
  ja_importado: boolean
  pedido_id_interno: number | null
}

export default function ImportarPedidoMLPage() {
  const nav = useNavigate()
  const [orderId, setOrderId] = useState('')
  const [preview, setPreview] = useState<Preview | null>(null)

  const buscar = useMutation({
    mutationFn: async (oid: string) => (await api.get<Preview>(`/ml/preview-pedido/${oid.trim()}`)).data,
    onSuccess: (d) => setPreview(d),
    onError: (e: any) => {
      setPreview(null)
      toast.error(e?.response?.data?.detail || 'Pedido não encontrado')
    },
  })

  const importar = useMutation({
    mutationFn: async (oid: string) => (await api.post(`/ml/importar-pedido-manual/${oid}`)).data,
    onSuccess: (d) => {
      const r = d.resultado || {}
      if (r.status === 'criado') {
        toast.success(`Pedido #${r.pedido_numero} importado!`)
        nav(`/vendas/pedidos/${r.pedido_id}`)
      } else if (r.status === 'ja_existe') {
        toast(`Já importado — pedido interno ${r.pedido_id}`)
        nav(`/vendas/pedidos/${r.pedido_id}`)
      } else {
        toast.error(`Pedido sem itens mapeáveis (anúncio não cadastrado?)`)
      }
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Falha ao importar'),
  })

  function onBuscar(e: React.FormEvent) {
    e.preventDefault()
    if (!orderId.trim()) return
    setPreview(null)
    buscar.mutate(orderId)
  }

  return (
    <div className="p-6 max-w-3xl space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-ink-900">Importar Pedido ML</h1>
        <p className="text-sm text-ink-500">
          Cole o número do pedido ML (order_id) e localize antes de importar.
        </p>
      </div>

      <form onSubmit={onBuscar} className="flex gap-2 items-stretch">
        <input
          type="text"
          value={orderId}
          onChange={(e) => setOrderId(e.target.value)}
          placeholder="Ex: 2000016133602208"
          className="input flex-1 font-mono"
          autoFocus
        />
        <button type="submit" disabled={!orderId.trim() || buscar.isPending}
          className="btn-primary flex items-center gap-1.5">
          {buscar.isPending ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
          Localizar
        </button>
      </form>

      {preview && (
        <div className={`border rounded-lg p-4 space-y-3 ${preview.ja_importado ? 'bg-amber-50 border-amber-300' : 'bg-emerald-50 border-emerald-300'}`}>
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2">
                {preview.ja_importado
                  ? <AlertCircle size={20} className="text-amber-600" />
                  : <CheckCircle2 size={20} className="text-emerald-600" />}
                <span className="font-mono text-sm font-semibold">{preview.order_id}</span>
                <a
                  href={`https://www.mercadolivre.com.br/vendas/${preview.order_id}/detalhe`}
                  target="_blank" rel="noreferrer"
                  className="text-blue-600 hover:underline text-xs flex items-center gap-0.5">
                  ver no ML <ExternalLink size={11} />
                </a>
              </div>
              <div className="text-xs text-ink-600 mt-1">
                {preview.buyer_nickname} · {preview.status_ml} · {preview.date_created?.slice(0, 10)}
              </div>
            </div>
            <div className="text-right">
              <div className="text-lg font-semibold">
                {preview.currency} {Number(preview.total).toFixed(2)}
              </div>
            </div>
          </div>

          <div className="border-t pt-2">
            <div className="text-xs font-semibold text-ink-700 mb-1">Itens:</div>
            <ul className="space-y-1">
              {preview.items.map((it, i) => (
                <li key={i} className="text-xs flex items-center justify-between gap-2 bg-white/60 rounded px-2 py-1">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{it.title}</div>
                    <div className="text-ink-500 font-mono">
                      {it.ml_item_id}{it.variation_id ? ` · var ${it.variation_id}` : ''}
                      {it.seller_sku ? ` · SKU ${it.seller_sku}` : ''}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div>{it.quantity}× {Number(it.unit_price).toFixed(2)}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {preview.ja_importado ? (
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm text-amber-800">
                Já importado como pedido interno <strong>#{preview.pedido_id_interno}</strong>.
              </span>
              <button onClick={() => nav(`/vendas/pedidos/${preview.pedido_id_interno}`)}
                className="btn-ghost text-xs">
                Abrir pedido
              </button>
            </div>
          ) : (
            <button onClick={() => importar.mutate(preview.order_id)}
              disabled={importar.isPending}
              className="btn-primary w-full flex items-center justify-center gap-1.5">
              {importar.isPending ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
              Importar este pedido
            </button>
          )}
        </div>
      )}
    </div>
  )
}
